import {AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, ViewChild,} from '@angular/core';
import {IProduct} from "../../product-models";
import {IShareLinkOptions} from "../../../social/social-models";
import {UsanaObject} from "../../../usana-object";
import {LoggerService} from "../../../common/logger.service";
import {CartUtil} from "../../../cart/cart-util";
import _ from "lodash";
import {Constants} from "../../../constants";
import {AddToCartDirective} from "../../../cart/add-to-cart/add-to-cart.directive";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import {UserService} from "../../../common/user.service";

@Component({
    selector: 'product-card-carousel',
    templateUrl: './product-card-carousel.component.html',
    styleUrls: ['./product-card-carousel.component.scss'],
    standalone: false
})
export class ProductCardCarouselComponent extends UsanaObject implements OnInit, OnDestroy, AfterViewInit {

    @Input() products: IProduct[];
    @Input() shareOptions: IShareLinkOptions;
    @Input() cardRowClass: string;
    @Input() showAddToCart: boolean = false;
    @ViewChild('carousel', {read: ElementRef}) carousel: ElementRef;

    slideItemChunkSize: number;
    slideItems: any[];
    activeSlideIndex: number = 0;
    productCount: number = 0;
    private resizeListener: () => void;

    constructor(
        loggerService: LoggerService,
        private addToCartDirective: AddToCartDirective,
        private renderer: Renderer2,
        private userService: UserService,
    ){
        super(loggerService);
    }

    ngOnInit() {
        this.slideItemChunkSize = this.getWindowChunkSize();
        this.slideItems = this.chunkSlideItems(this.slideItemChunkSize);
        this.setupResizeListener();
    }

    ngAfterViewInit() {
        this.addAriaLabelsToCarouselIndicators();
    }

    ngOnDestroy() {
        window.removeEventListener('resize', this.resizeListener);
    }

    addProductToCart(product: IProduct, btnElement: HTMLButtonElement) {
        // using addToCartDirective so it matches all other product card instances
        this.addToCartDirective.quantity = 1;
        this.addToCartDirective.product = product;
        this.addToCartDirective.buttonElement = btnElement;
        this.addToCartDirective.addToCart();
    }

    showAddToCartButton$(product: IProduct): Observable<boolean> {
        return this.userService.getShopUser().pipe(
            map(user => CartUtil.showAddToCartButtonProduct(product, user))
        );
    }

    chunkSlideItems(chunkSize: number): any[] {
        if (this.products) {
            const chunkedItems = _.chunk(this.products, chunkSize);
            // Ensure there's an active slide, if not, set activeSlideIndex to 0
            if (this.activeSlideIndex >= chunkedItems.length) {
                this.activeSlideIndex = 0;
            }
            return chunkedItems;
        }
    }

    getWindowChunkSize(): number {
        const width = window.innerWidth;
        // 1200 is the breakpoint for desktop and 768 is the breakpoint for tablet, else it's mobile which should be all products on one slide
        if (width > 1200) {
            return Constants.MAX_FEATURED_LANDING_COUNT_DESKTOP;
        } else if (width >= 768 && width <= 1200) {
            return Constants.MAX_FEATURED_LANDING_COUNT_TABLET;
        } else {
            return Constants.MAX_FEATURED_LANDING_COUNT_MOBILE;
        }
    }

    setupResizeListener(): void {
        this.resizeListener = _.debounce(() => {
            const previousChunkSize = this.slideItemChunkSize;
            this.slideItemChunkSize = this.getWindowChunkSize();
            this.slideItems = this.chunkSlideItems(this.slideItemChunkSize);

            // If the chunk size changes, potentially adjust the activeSlideIndex
            if (previousChunkSize !== this.slideItemChunkSize) {
                this.updateActiveSlideIndex();
            }
        }, 150);

        window.addEventListener('resize', this.resizeListener);
    }

    updateActiveSlideIndex(): void {
        this.activeSlideIndex = Math.min(this.activeSlideIndex, this.slideItems.length - 1);
    }

    addAriaLabelsToCarouselIndicators(): void {
        // Select the dynamically generated carousel indicator buttons
        const indicators = this.carousel.nativeElement.querySelectorAll('.carousel-indicators button');

        if (indicators.length > 1) {
            indicators.forEach((button: HTMLElement, index: number) => {
                // Use Renderer2 to set the aria-label
                this.renderer.setAttribute(button, 'aria-label', `Slide ${index + 1}`);
            });
        }
    }


}
